import type { SvgIconComponent } from '@mui/icons-material';
import {
  DashboardOutlined,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon,
  BugReport as BugReportIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import type { Me } from '../../modules/admin/types';
import { isContractReviewEnabled, isIrmtEnabled, isIrisEnabled, isBaselineSecurityEnabled, isThreatHuntEnabled } from '../utils/features';
import {
  canAccessContractReview,
  canAccessQuestionnaire,
  canAccessRiskManagement,
  canAccessBaselineSecurity,
  canAccessSecurityIncident,
  canAccessThreatHunt,
} from '../permissions/capabilities';

export interface NavContext {
  me: Me | null;
}

export type NavSectionId = 'main' | 'modules' | 'system';

export const NAV_SECTION_LABELS: Record<NavSectionId, string> = {
  main: 'Main',
  modules: 'Modules',
  system: 'System',
};

const NAV_SECTION_ORDER: readonly NavSectionId[] = ['main', 'modules', 'system'];

export interface AppNavItem {
  /** Stable id for React keys and analytics */
  id: string;
  label: string;
  /** Exact path for navigation; use trailing routes consistently (no trailing slash) */
  path: string;
  icon: SvgIconComponent;
  section: NavSectionId;
  /** Whether this item appears in the sidebar for the current user / flags */
  isVisible: (ctx: NavContext) => boolean;
}

export const APP_NAV_ITEMS: readonly AppNavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: DashboardOutlined,
    section: 'main',
    isVisible: () => true,
  },
  {
    id: 'security-assessment',
    label: 'Security Assessment',
    path: '/questionnaire',
    icon: SecurityIcon,
    section: 'modules',
    isVisible: (ctx) => canAccessQuestionnaire(ctx.me),
  },
  {
    id: 'contract-review',
    label: 'Contract Review',
    path: '/contract-review',
    icon: DescriptionIcon,
    section: 'modules',
    isVisible: (ctx) =>
      isContractReviewEnabled() && canAccessContractReview(ctx.me),
  },
  {
    id: 'risk-management',
    label: 'Risk Management',
    path: '/risk-management',
    icon: AssessmentIcon,
    section: 'modules',
    isVisible: (ctx) => isIrmtEnabled() && canAccessRiskManagement(ctx.me),
  },
  {
    id: 'security-incident',
    label: 'Security Incident',
    path: '/security-incident',
    icon: InsightsIcon,
    section: 'modules',
    isVisible: (ctx) => isIrisEnabled() && canAccessSecurityIncident(ctx.me),
  },
  {
    id: 'threat-hunt',
    label: 'Threat Hunt',
    path: '/threat-hunt',
    icon: BugReportIcon,
    section: 'modules',
    isVisible: (ctx) => isThreatHuntEnabled() && canAccessThreatHunt(ctx.me),
  },
  {
    id: 'baseline-security',
    label: 'Baseline Security',
    path: '/baseline-security',
    icon: ShieldIcon,
    section: 'modules',
    isVisible: (ctx) => isBaselineSecurityEnabled() && canAccessBaselineSecurity(ctx.me),
  },
  {
    id: 'administration',
    label: 'Administration',
    path: '/admin',
    icon: GroupsIcon,
    section: 'system',
    isVisible: (ctx) => Boolean(ctx.me?.can_manage_roles || ctx.me?.can_manage_users),
  },
] as const;

export function getVisibleNavItems(ctx: NavContext): AppNavItem[] {
  return APP_NAV_ITEMS.filter((item) => item.isVisible(ctx));
}

export function getVisibleNavSections(ctx: NavContext): { section: NavSectionId; items: AppNavItem[] }[] {
  const visible = getVisibleNavItems(ctx);
  return NAV_SECTION_ORDER.map((section) => ({
    section,
    items: visible.filter((item) => item.section === section),
  })).filter((g) => g.items.length > 0);
}

export function isNavItemActive(pathname: string, item: AppNavItem): boolean {
  if (item.path === '/') {
    return pathname === '/';
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}
